// src/pages/POS.js
//
// Punto de venta operativo real.
//
// Requisitos previos: caja con turno abierto en la sucursal activa.
//   - Si NO hay turno: se muestra alerta con link a /caja.
//   - Si hay turno colgado (día anterior): alerta bloqueante.
//
// Flujo:
//   1. Cargar productos con stock del almacén asociado a la caja abierta.
//   2. Buscar/agregar al carrito, ajustar cantidad, aplicar descuento.
//   3. Seleccionar cliente (por defecto Consumidor Final).
//   4. Cobrar → dialog de pago con métodos reales.
//   5. Registrar venta atómica → backend heredado /api/pos/ventas.
//   6. Mostrar ticket con opción "Nueva venta".
//
// Consume: caja, ventas, productos.conStockDeAlmacen, clientes,
//          catalogo.categorias, catalogo.marcas.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Grid, Card, Typography, Button, Stack, Divider, Paper, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  MenuItem, IconButton, Tooltip, CircularProgress, Autocomplete, Avatar,
  LinearProgress,
} from '@mui/material';
import SearchIcon             from '@mui/icons-material/Search';
import AddIcon                from '@mui/icons-material/Add';
import RemoveIcon             from '@mui/icons-material/Remove';
import DeleteIcon             from '@mui/icons-material/Delete';
import PersonIcon             from '@mui/icons-material/Person';
import PaymentsIcon           from '@mui/icons-material/Payments';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import PointOfSaleIcon        from '@mui/icons-material/PointOfSale';
import LockIcon               from '@mui/icons-material/Lock';
import WarningIcon            from '@mui/icons-material/Warning';
import ReceiptLongIcon        from '@mui/icons-material/ReceiptLong';
import PrintIcon              from '@mui/icons-material/Print';
import { jsPDF as jsPDFTicket } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { caja as apiCaja, ventas as apiVentas, productos as apiProds,
         clientes as apiClientes, catalogo as apiCat } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

const POS = () => {
  const { empresa, sucursalActivaId, notify } = useApp();
  const navigate = useNavigate();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  // ── Estado principal ─────────────────────────────────────
  const [caja, setCaja] = useState(null);           // caja abierta activa
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cargandoProds, setCargandoProds] = useState(false);

  // ── Carrito y venta ──────────────────────────────────────
  const [carrito, setCarrito] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [ventaCompletada, setVentaCompletada] = useState(null);

  // ── Dialogs ──────────────────────────────────────────────
  const [pagoOpen, setPagoOpen] = useState(false);
  const [clienteOpen, setClienteOpen] = useState(false);

  const busquedaRef = useRef(null);

  // ── Carga inicial: caja + productos + categorías + métodos + cliente CF
  const cargarInicial = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      // 1. Cajas de la sucursal (para detectar turno abierto)
      const cajas = await apiCaja.listarCajas(empresaId, sucursalActivaId);
      const cajaAbierta = (Array.isArray(cajas) ? cajas : [])
        .find(c => c.aperturaActiva) || null;
      setCaja(cajaAbierta);

      // 2. Métodos de pago
      const met = await apiCaja.listarMetodosPago(empresaId);
      setMetodos(Array.isArray(met) ? met : []);

      // 3. Consumidor Final por default
      const cf = await apiClientes.consumidorFinal(empresaId);
      setCliente(cf);

      // 4. Categorías (para chips filtro)
      try {
        const cats = await apiCat.categorias.listar(empresaId);
        setCategorias(Array.isArray(cats) ? cats : []);
      } catch { /* opcional */ }

      // 5. Productos del almacén de la caja abierta
      if (cajaAbierta?.almacenId) {
        await cargarProductosPorAlmacen(cajaAbierta.almacenId);
      } else {
        setProductos([]);
      }
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar el POS.', 'error');
    } finally { setCargando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, sucursalActivaId]);

  const cargarProductosPorAlmacen = async (almacenId) => {
    setCargandoProds(true);
    try {
      const r = await apiProds.conStockDeAlmacen(almacenId, { pageSize: 500 });
      const items = r?.data || r?.items || r || [];
      setProductos(Array.isArray(items) ? items : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar productos.', 'error');
    } finally { setCargandoProds(false); }
  };

  useEffect(() => { cargarInicial(); }, [cargarInicial]);

  // ── Productos filtrados ──────────────────────────────────
  const productosFiltrados = useMemo(() => {
    let arr = productos;
    if (categoriaId) {
      arr = arr.filter(p => p.categoriaId === categoriaId
                          || p.categoriaCatalogoId === categoriaId);
    }
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      arr = arr.filter(p =>
        (p.nombre || '').toLowerCase().includes(q)
        || (p.codigo || '').toLowerCase().includes(q)
        || (p.codigoBarras || '').toLowerCase() === q
      );
    }
    return arr.slice(0, 120);   // límite visual
  }, [productos, categoriaId, busqueda]);

  // ── Carrito ──────────────────────────────────────────────
  const agregarAlCarrito = (p) => {
    if (p.controlaInventario && (p.stockDisponible || 0) <= 0) {
      notify(`Sin stock de ${p.nombre}.`, 'warning'); return;
    }
    setCarrito(prev => {
      const i = prev.findIndex(x => x.productoId === p.productoId);
      if (i >= 0) {
        const c = [...prev];
        const nueva = c[i].cantidad + 1;
        if (p.controlaInventario && nueva > (p.stockDisponible || 0)) {
          notify('Stock insuficiente.', 'warning'); return prev;
        }
        c[i] = { ...c[i], cantidad: nueva };
        return c;
      }
      return [...prev, {
        productoId: p.productoId,
        codigo: p.codigo,
        nombre: p.nombre,
        precioUnitario: Number(p.precioVenta || 0),
        porcentajeIVA: Number(p.porcentajeIVA || 0),
        cantidad: 1,
        porcentajeDescuento: 0,
        stockDisponible: p.stockDisponible || 0,
        controlaInventario: p.controlaInventario ?? true,
      }];
    });
    setBusqueda('');
    busquedaRef.current?.focus();
  };

  const cambiarCantidad = (i, delta) => {
    setCarrito(prev => {
      const c = [...prev];
      const nueva = c[i].cantidad + delta;
      if (nueva <= 0) return c.filter((_, x) => x !== i);
      if (c[i].controlaInventario && nueva > c[i].stockDisponible) {
        notify('Stock insuficiente.', 'warning'); return c;
      }
      c[i] = { ...c[i], cantidad: nueva }; return c;
    });
  };

  const setDescuento = (i, pct) => {
    setCarrito(prev => {
      const c = [...prev];
      c[i] = { ...c[i], porcentajeDescuento: Math.max(0, Math.min(100, Number(pct) || 0)) };
      return c;
    });
  };

  const eliminarLinea = (i) => setCarrito(prev => prev.filter((_, x) => x !== i));

  const totales = useMemo(() => {
    let subtotal = 0, descuento = 0, iva = 0;
    carrito.forEach(l => {
      const bruto = l.precioUnitario * l.cantidad;
      const desc = bruto * (l.porcentajeDescuento / 100);
      const base = bruto - desc;
      const ivaLin = base * (l.porcentajeIVA / 100);
      subtotal += bruto; descuento += desc; iva += ivaLin;
    });
    return { subtotal, descuento, iva, total: Math.round(subtotal - descuento + iva) };
  }, [carrito]);

  const handleEnter = (e) => {
    if (e.key !== 'Enter' || !busqueda) return;
    // Código de barras exacto
    if (/^\d{6,}$/.test(busqueda)) {
      const p = productos.find(x => x.codigoBarras === busqueda);
      if (p) { agregarAlCarrito(p); return; }
    }
    // O primer producto filtrado
    if (productosFiltrados.length > 0) agregarAlCarrito(productosFiltrados[0]);
  };

  // ── Registrar venta ──────────────────────────────────────
  const registrarVenta = async (pagos) => {
    if (!caja || !caja.aperturaActiva) {
      notify('No hay turno de caja abierto.', 'error'); return;
    }
    if (carrito.length === 0) { notify('El carrito está vacío.', 'warning'); return; }

    const dto = {
      empresaId,
      sucursalId: caja.sucursalId,
      cajaId: caja.cajaId,
      aperturaCajaId: caja.aperturaActiva.aperturaCajaId,
      almacenId: caja.almacenId,
      clienteId: cliente?.clienteId,
      clienteNombre: cliente?.nombreCompleto || cliente?.primerNombre,
      clienteDocumento: cliente?.numeroDocumento,
      tipoVenta: pagos.some(p => {
        const m = metodos.find(x => x.metodoPagoId === p.metodoPagoId);
        return m?.tipoPago === 'CREDITO';
      }) ? 'CREDITO' : 'MOSTRADOR',
      requiereFactura: false,
      acumularPuntos: false,
      puntosCanjeados: 0,
      diasCredito: 30, numeroCuotas: 1,
      items: carrito.map(l => ({
        productoId: l.productoId,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        porcentajeDescuento: l.porcentajeDescuento,
      })),
      pagos: pagos.map(p => ({
        metodoPagoId: p.metodoPagoId, monedaId: 1,
        monto: Number(p.monto),
        montoRecibido: p.montoRecibido != null ? Number(p.montoRecibido) : null,
        referencia: p.referencia || null,
      })),
    };

    try {
      const r = await apiVentas.registrar(dto);
      setVentaCompletada(r);
      setPagoOpen(false);
      notify(`Venta ${r.numeroTicket} registrada.`, 'success');
      // Refrescar stock local
      if (caja?.almacenId) await cargarProductosPorAlmacen(caja.almacenId);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo registrar la venta.', 'error');
    }
  };

  const nuevaVenta = () => {
    setCarrito([]);
    setVentaCompletada(null);
    setBusqueda('');
    busquedaRef.current?.focus();
  };

  // ══════════ RENDER ══════════

  if (cargando) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  // Sin turno abierto — bloquea el POS
  if (!caja) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Sin turno de caja abierto</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Para facturar necesitas abrir un turno de caja en la sucursal activa.
        </Typography>
        <Button variant="contained" startIcon={<PointOfSaleIcon />}
                onClick={() => navigate('/caja')}>
          Ir a Caja
        </Button>
      </Card>
    );
  }

  // Ticket post-venta
  if (ventaCompletada) {
    return <TicketVenta venta={ventaCompletada} onNueva={nuevaVenta} />;
  }

  return (
    <Box>
      {/* Header caja */}
      <Card sx={{ p: 2, mb: 2, background: 'linear-gradient(90deg,#e8f5e9,#f1f8e9)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label="TURNO ABIERTO" color="success" />
              <Typography variant="body2" fontWeight={600}>
                {caja.nombre} · {caja.sucursalNombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cajero: {caja.aperturaActiva.nombreUsuario}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
            <Button size="small" onClick={() => navigate('/caja')}>Gestión de caja</Button>
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={2}>
        {/* IZQ: catálogo */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              <TextField fullWidth inputRef={busquedaRef} autoFocus
                         placeholder="Buscar por nombre, código o código de barras (Enter para agregar)"
                         value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                         onKeyDown={handleEnter}
                         InputProps={{
                           startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                         }} />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip label="Todas" size="small"
                    color={!categoriaId ? 'primary' : 'default'}
                    onClick={() => setCategoriaId('')} />
              {categorias.slice(0, 8).map(c => (
                <Chip key={c.categoriaId || c.categoriaCatalogoId} label={c.nombre} size="small"
                      color={categoriaId === (c.categoriaId || c.categoriaCatalogoId) ? 'primary' : 'default'}
                      onClick={() => setCategoriaId(c.categoriaId || c.categoriaCatalogoId)} />
              ))}
            </Stack>
            {cargandoProds ? (
              <LinearProgress />
            ) : productosFiltrados.length === 0 ? (
              <Alert severity="info">
                {productos.length === 0
                  ? 'No hay productos en el almacén de esta caja.'
                  : 'Sin productos que coincidan.'}
              </Alert>
            ) : (
              <Grid container spacing={1}>
                {productosFiltrados.map(p => {
                  const sinStock = p.controlaInventario && (p.stockDisponible || 0) <= 0;
                  return (
                    <Grid item xs={6} sm={4} md={4} key={p.productoId}>
                      <Paper
                        variant="outlined"
                        onClick={() => !sinStock && agregarAlCarrito(p)}
                        sx={{
                          p: 1.5, cursor: sinStock ? 'not-allowed' : 'pointer',
                          opacity: sinStock ? 0.5 : 1,
                          '&:hover': { boxShadow: sinStock ? 0 : 3 },
                        }}>
                        <Typography variant="body2" fontWeight={500} noWrap>{p.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {p.codigo}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                          <Typography variant="body2" fontWeight={700} color="primary">
                            {fmtCOP(p.precioVenta)}
                          </Typography>
                          {p.controlaInventario && (
                            <Chip size="small" variant="outlined"
                                  label={`Stock ${p.stockDisponible}`}
                                  color={sinStock ? 'error' : p.stockDisponible < 5 ? 'warning' : 'default'} />
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Card>
        </Grid>

        {/* DER: carrito */}
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2, position: 'sticky', top: 16 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PointOfSaleIcon color="primary" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Venta actual</Typography>
              <Chip size="small" label={`${carrito.length} línea${carrito.length === 1 ? '' : 's'}`} />
            </Stack>

            {/* Cliente */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {cliente?.nombreCompleto || 'Consumidor Final'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cliente?.numeroDocumento}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setClienteOpen(true)}>Cambiar</Button>
            </Paper>

            {/* Líneas */}
            {carrito.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>Agrega productos desde el catálogo o escanea el código.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 340, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="center" width={110}>Cantidad</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right" width={40}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {carrito.map((l, i) => {
                      const totalLinea = l.precioUnitario * l.cantidad * (1 - l.porcentajeDescuento / 100)
                                       * (1 + l.porcentajeIVA / 100);
                      return (
                        <TableRow key={l.productoId}>
                          <TableCell>
                            <Typography variant="caption" fontWeight={500} display="block">{l.nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">{fmtCOP(l.precioUnitario)}</Typography>
                            <TextField type="number" size="small" placeholder="Desc %"
                                       value={l.porcentajeDescuento || ''}
                                       onChange={(e) => setDescuento(i, e.target.value)}
                                       sx={{ width: 90, mt: 0.5 }}
                                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                              <IconButton size="small" onClick={() => cambiarCantidad(i, -1)}><RemoveIcon fontSize="small" /></IconButton>
                              <Typography variant="body2" fontWeight={600}>{l.cantidad}</Typography>
                              <IconButton size="small" onClick={() => cambiarCantidad(i, 1)}><AddIcon fontSize="small" /></IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell align="right"><b>{fmtCOP(totalLinea)}</b></TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => eliminarLinea(i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Totales */}
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{fmtCOP(totales.subtotal)}</Typography>
              </Stack>
              {totales.descuento > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Descuento</Typography>
                  <Typography variant="body2" color="text.secondary">−{fmtCOP(totales.descuento)}</Typography>
                </Stack>
              )}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">IVA</Typography>
                <Typography variant="body2">{fmtCOP(totales.iva)}</Typography>
              </Stack>
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="h6">Total</Typography>
                <Typography variant="h5" fontWeight={700} color="primary">{fmtCOP(totales.total)}</Typography>
              </Stack>
            </Stack>

            <Button fullWidth size="large" variant="contained" color="success"
                    startIcon={<PaymentsIcon />} sx={{ mt: 2 }}
                    disabled={carrito.length === 0}
                    onClick={() => setPagoOpen(true)}>
              Cobrar
            </Button>
          </Card>
        </Grid>
      </Grid>

      {pagoOpen && (
        <DialogCobro total={totales.total} metodos={metodos}
                     cliente={cliente}
                     onClose={() => setPagoOpen(false)}
                     onCobrar={registrarVenta} />
      )}
      {clienteOpen && (
        <DialogSeleccionarCliente empresaId={empresaId}
                                  onClose={() => setClienteOpen(false)}
                                  onElegir={(c) => { setCliente(c); setClienteOpen(false); }} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Cobro
// ══════════════════════════════════════════════════════════════════════

function DialogCobro({ total, metodos, cliente, onClose, onCobrar }) {
  const efectivo = metodos.find(m => m.tipoPago === 'EFECTIVO');
  const [pagos, setPagos] = useState([{
    metodoPagoId: efectivo?.metodoPagoId || (metodos[0]?.metodoPagoId || ''),
    monto: String(total),
    montoRecibido: '',
    referencia: '',
  }]);
  const [g, setG] = useState(false);

  const totalPagado = pagos.reduce((s, p) => s + (Number(p.monto) || 0), 0);
  const difPagado = totalPagado - total;

  const actualizar = (i, campo, valor) => {
    setPagos(prev => {
      const c = [...prev]; c[i] = { ...c[i], [campo]: valor }; return c;
    });
  };
  const agregarPago = () => {
    setPagos(prev => [...prev, {
      metodoPagoId: metodos[0]?.metodoPagoId || '',
      monto: String(Math.max(0, total - totalPagado)),
      montoRecibido: '', referencia: '',
    }]);
  };
  const quitarPago = (i) => setPagos(prev => prev.filter((_, x) => x !== i));

  const submit = async () => {
    if (Math.abs(difPagado) > 0.01) return;
    for (const p of pagos) {
      const m = metodos.find(x => x.metodoPagoId === Number(p.metodoPagoId));
      if (m?.requiereReferencia && !p.referencia.trim()) return;
      if (m?.tipoPago === 'CREDITO' && !cliente?.tieneCredito) return;
    }
    setG(true);
    try { await onCobrar(pagos); }
    finally { setG(false); }
  };

  const puedeCobrar = Math.abs(difPagado) < 0.01 && pagos.every(p => Number(p.monto) > 0)
    && pagos.every(p => {
      const m = metodos.find(x => x.metodoPagoId === Number(p.metodoPagoId));
      if (m?.requiereReferencia && !p.referencia.trim()) return false;
      if (m?.tipoPago === 'CREDITO' && !cliente?.tieneCredito) return false;
      return true;
    });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Cobrar {fmtCOP(total)}
        <Typography variant="caption" display="block" color="text.secondary">
          Cliente: {cliente?.nombreCompleto || 'Consumidor Final'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {pagos.map((p, i) => {
            const m = metodos.find(x => x.metodoPagoId === Number(p.metodoPagoId));
            const esEfectivo = m?.tipoPago === 'EFECTIVO';
            const cambio = esEfectivo && p.montoRecibido
              ? Number(p.montoRecibido) - Number(p.monto) : 0;
            return (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField select size="small" label="Método" sx={{ flexGrow: 1 }}
                             value={p.metodoPagoId}
                             onChange={(e) => actualizar(i, 'metodoPagoId', Number(e.target.value))}>
                    {metodos.map(mm => (
                      <MenuItem key={mm.metodoPagoId} value={mm.metodoPagoId}>{mm.nombre}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" type="number" label="Monto" sx={{ width: 140 }}
                             value={p.monto} onChange={(e) => actualizar(i, 'monto', e.target.value)}
                             InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                  {pagos.length > 1 && (
                    <IconButton size="small" onClick={() => quitarPago(i)}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Stack>
                {esEfectivo && (
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" type="number" label="Recibido" sx={{ width: 140 }}
                               value={p.montoRecibido}
                               onChange={(e) => actualizar(i, 'montoRecibido', e.target.value)}
                               InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    {cambio > 0 && (
                      <Alert severity="success" sx={{ flexGrow: 1, py: 0 }}>
                        Cambio: <b>{fmtCOP(cambio)}</b>
                      </Alert>
                    )}
                  </Stack>
                )}
                {m?.requiereReferencia && (
                  <TextField size="small" fullWidth label="Referencia" sx={{ mt: 1 }}
                             value={p.referencia}
                             onChange={(e) => actualizar(i, 'referencia', e.target.value)} />
                )}
                {m?.tipoPago === 'CREDITO' && !cliente?.tieneCredito && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Este cliente no tiene crédito habilitado. Elige otro método o cambia de cliente.
                  </Alert>
                )}
              </Paper>
            );
          })}
          <Button size="small" onClick={agregarPago} startIcon={<AddIcon />}>
            Agregar otro método
          </Button>

          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography>Total a pagar:</Typography>
            <Typography fontWeight={700}>{fmtCOP(total)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography>Total ingresado:</Typography>
            <Typography fontWeight={700}
                        color={Math.abs(difPagado) < 0.01 ? 'success.main' : 'error.main'}>
              {fmtCOP(totalPagado)}
            </Typography>
          </Stack>
          {Math.abs(difPagado) >= 0.01 && (
            <Alert severity="warning">
              Diferencia: {fmtCOP(difPagado)}. Ajusta los montos para cuadrar el total.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={submit}
                disabled={!puedeCobrar || g} startIcon={<CheckCircleIcon />}>
          {g ? 'Registrando…' : 'Confirmar venta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Seleccionar cliente
// ══════════════════════════════════════════════════════════════════════

function DialogSeleccionarCliente({ empresaId, onClose, onElegir }) {
  const [q, setQ] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setOpciones([]); return; }
    const timer = setTimeout(async () => {
      setCargando(true);
      try {
        const r = await apiClientes.listar(empresaId, { q: q.trim(), pageSize: 10 });
        setOpciones(r.data || []);
      } catch { /* silent */ }
      finally { setCargando(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, empresaId]);

  const usarCF = async () => {
    try { onElegir(await apiClientes.consumidorFinal(empresaId)); } catch { /* silent */ }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Seleccionar cliente</DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth autoFocus size="small" placeholder="Buscar por nombre, documento, teléfono"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Box sx={{ mt: 2 }}>
          {cargando && <LinearProgress />}
          {opciones.map(c => (
            <Paper key={c.clienteId} variant="outlined"
                   onClick={() => onElegir(c)}
                   sx={{ p: 1.5, mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}>
              <Typography variant="body2" fontWeight={500}>{c.nombreCompleto}</Typography>
              <Typography variant="caption" color="text.secondary">
                {c.numeroDocumento}
                {c.celular ? ` · ${c.celular}` : ''}
                {c.tieneCredito ? ' · Con crédito' : ''}
                {c.saldoActual > 0 ? ` · Saldo ${fmtCOP(c.saldoActual)}` : ''}
              </Typography>
            </Paper>
          ))}
          {q && !cargando && opciones.length === 0 && (
            <Alert severity="info">Sin resultados.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={usarCF}>Usar Consumidor Final</Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Ticket post-venta
// ══════════════════════════════════════════════════════════════════════

/**
 * Genera un PDF con formato de ticket térmico (80mm de ancho) y lo
 * descarga/abre. El tendero lo imprime desde el visor de PDF con la
 * impresora térmica configurada.
 */
function generarTicketPDF(venta, empresa, sucursal) {
  const anchoMM = 80;
  // Altura estimada dinámica: cabecera 60 + items*8 + pagos*6 + totales+footer 50
  const items = venta.items || venta.detalle || [];
  const pagos = venta.pagos || [];
  const altoMM = 60 + items.length * 8 + pagos.length * 6 + 50;

  const doc = new jsPDFTicket({ orientation: 'p', unit: 'mm', format: [anchoMM, altoMM] });
  const cx = anchoMM / 2;
  let y = 6;
  const line = (txt, opts = {}) => {
    doc.setFontSize(opts.size || 8);
    doc.setFont(opts.font || 'helvetica', opts.bold ? 'bold' : 'normal');
    doc.text(String(txt), opts.x ?? cx, y, { align: opts.align || 'center' });
    y += opts.dy || 4;
  };
  const hr = () => {
    doc.setLineDash([0.5, 0.5], 0); doc.setLineWidth(0.1);
    doc.line(3, y, anchoMM - 3, y); y += 3; doc.setLineDash([]);
  };
  const fmt = (n) => (Number(n) || 0).toLocaleString('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0
  });

  // Cabecera
  line(empresa?.razonSocial || empresa?.RazonSocial || 'Empresa', { size: 10, bold: true, dy: 5 });
  if (empresa?.nombreComercial || empresa?.NombreComercial)
    line(empresa.nombreComercial || empresa.NombreComercial, { size: 8 });
  if (empresa?.numeroDocumento || empresa?.NumeroDocumento)
    line(`NIT ${empresa.numeroDocumento || empresa.NumeroDocumento}`, { size: 7 });
  if (sucursal?.nombre) line(sucursal.nombre, { size: 7 });
  if (sucursal?.direccion) line(sucursal.direccion, { size: 7 });
  if (sucursal?.telefono) line(`Tel: ${sucursal.telefono}`, { size: 7 });
  hr();

  // Datos venta
  line(`TICKET ${venta.numeroTicket || venta.numero || ''}`, { size: 9, bold: true });
  line(new Date(venta.fechaVenta || venta.fecha || Date.now()).toLocaleString('es-CO'), { size: 7 });
  if (venta.clienteNombre) line(`Cliente: ${venta.clienteNombre}`, { size: 7 });
  if (venta.clienteDocumento) line(`Doc: ${venta.clienteDocumento}`, { size: 7 });
  hr();

  // Detalle
  doc.setFontSize(7); doc.setFont('helvetica', 'bold');
  doc.text('Producto', 3, y);
  doc.text('Cant', 45, y);
  doc.text('Precio', 55, y, { align: 'right' });
  doc.text('Total', anchoMM - 3, y, { align: 'right' });
  y += 4;
  doc.setFont('helvetica', 'normal');
  items.forEach(it => {
    const nombre = String(it.nombreProducto || it.nombre || '').substring(0, 25);
    const cant = it.cantidad || 0;
    const pu = it.precioUnitario || 0;
    const total = (it.total != null ? it.total : pu * cant);
    doc.text(nombre, 3, y);
    doc.text(String(cant), 45, y);
    doc.text(fmt(pu), 55, y, { align: 'right' });
    doc.text(fmt(total), anchoMM - 3, y, { align: 'right' });
    y += 4;
  });
  hr();

  // Totales
  doc.setFontSize(8);
  const totalItem = (lbl, val, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 10 : 8);
    doc.text(lbl, 3, y);
    doc.text(fmt(val), anchoMM - 3, y, { align: 'right' });
    y += bold ? 5 : 4;
  };
  if (venta.subtotal != null) totalItem('Subtotal', venta.subtotal);
  if (venta.descuentoTotal > 0) totalItem('Descuento', -venta.descuentoTotal);
  if (venta.iva > 0) totalItem('IVA', venta.iva);
  totalItem('TOTAL', venta.totalRedondeado || venta.total, true);
  hr();

  // Pagos
  if (pagos.length > 0) {
    doc.setFontSize(7); doc.setFont('helvetica', 'bold');
    doc.text('Forma de pago', 3, y); y += 4;
    doc.setFont('helvetica', 'normal');
    pagos.forEach(p => {
      doc.text(String(p.metodoNombre || p.metodo || 'Pago'), 3, y);
      doc.text(fmt(p.monto), anchoMM - 3, y, { align: 'right' });
      y += 3.5;
    });
    hr();
  }

  // Footer
  doc.setFontSize(7); doc.setFont('helvetica', 'normal');
  y += 2;
  doc.text('¡Gracias por tu compra!', cx, y, { align: 'center' }); y += 4;
  doc.text('Ticket generado por HolaVeci Local', cx, y, { align: 'center' });

  doc.autoPrint();   // abre el diálogo de impresión automáticamente
  doc.save(`ticket_${venta.numeroTicket || Date.now()}.pdf`);
}

function TicketVenta({ venta, onNueva }) {
  const { empresa, sucursalActiva } = useApp();
  const imprimir = () => {
    try { generarTicketPDF(venta, empresa, sucursalActiva); }
    catch { /* silent */ }
  };
  return (
    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>¡Venta registrada!</Typography>
        <Typography variant="h4" color="primary" sx={{ my: 2 }}>{venta.numeroTicket}</Typography>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1} sx={{ textAlign: 'left' }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography>Cliente:</Typography>
            <Typography fontWeight={500}>{venta.clienteNombre || 'Consumidor Final'}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography>Total:</Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {fmtCOP(venta.totalRedondeado || venta.total)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography>Estado:</Typography>
            <Chip size="small" label={venta.estadoPago}
                  color={venta.estadoPago === 'PAGADO' ? 'success' : 'warning'} />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button fullWidth size="large" variant="outlined"
                  startIcon={<PrintIcon />} onClick={imprimir}>
            Imprimir ticket
          </Button>
          <Button fullWidth size="large" variant="contained"
                  startIcon={<ReceiptLongIcon />} onClick={onNueva}>
            Nueva venta
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

export default POS;
